import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import {
  useUpdateCustomRoles,
  useUpdateUserRoles,
} from '@/hooks/mutations/user';
import {
  useGetAuthUserByIdWithRoles,
  useGetAvailableRoles,
  useGetCustomRoles,
} from '@/hooks/queries';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import UserDetailsForm from './forms/UserDetailsForm';
import type {
  UserDetailsFormFields,
  UserDetailsFormFieldsMultiRole,
} from './forms/UserDetailsSchema';
import {
  UserDetailsSchema,
  UserDetailsSchemaMultiRole,
} from './forms/UserDetailsSchema';

type Props = {
  onDismiss: () => void;
  id: string;
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUUID = (value: string) => UUID_REGEX.test(value);

const UserDetailsModal: FC<Props> = ({ onDismiss, id }) => {
  const { t } = useTranslation();
  const { updateUserRoles } = useUpdateUserRoles();
  const { updateCustomRoles } = useUpdateCustomRoles();

  // Check if the new granular role system (trpc feature) is enabled
  const isMultiRoleEnabled = useIsFeatureFlagEnabled('trpc');

  const callerIsCS = useIsCustomerSupport();

  const { data: getUserResponse, loading: loadingUser } =
    useGetAuthUserByIdWithRoles({
      queryArgs: { id },
      shouldSkip: !id,
    });
  const user = getUserResponse?.auth_user_by_pk;
  const userOrg = user?.organisationusers[0];

  // V3 assigns roles through CUSTOM ROLES only. The modal deliberately does not
  // read `user.userRoles` or call `updateUserRoles` on this path (COI-568):
  //
  // `auth.user_role` is provenance-tagged and mixed — alongside the picker's
  // `direct` rows it holds `custom_role` rows owned by the reconcile triggers
  // and `auto` rows owned by `auth.auto_assign_role()`. Presenting that list as
  // the picker's selection misrepresents derived grants as directly assigned,
  // and feeding it back into a full-replace diff is what emptied a user's nav:
  // the list was snapshotted before `updateCustomRoles` ran, so the save deleted
  // the grants its own first call had just created.
  //
  // The old system-roles sub-picker (CS assigning e.g. CustomerSuccess to a
  // RiskSmart-internal user) is gone from V3 for the same reason. It is not a
  // capability loss that matters: `CustomerSuccess` is now derived from
  // `organisationuser."RoleKey" = 'CustomerSupport'` and written as `auto`, so
  // org membership grants it without anyone picking it. `updateUserRoles`
  // remains available as a tRPC procedure for SCIM and other API callers.

  // Prepare form values based on whether multi-role mode is enabled
  const userValues: UserDetailsFormFields | UserDetailsFormFieldsMultiRole =
    isMultiRoleEnabled
      ? {
          UserId: user?.Id ?? '',
          Roles: [
            // Existing custom roles
            ...(user?.customRoles?.map((cr) => ({
              label: cr.role.RoleName,
              value: cr.role.Id,
              description: cr.role.Description ?? '',
            })) ?? []),
          ],
        }
      : {
          UserId: user?.Id ?? '',
          // COI-824: this org's membership role only. The dropped
          // `?? user?.RoleKey` fallback read the tenant-global
          // `auth.user."RoleKey"`, which holds whatever org wrote it last — so
          // on a user whose membership had no role recorded, this form
          // pre-selected ANOTHER org's role and an unedited save persisted it
          // here. That is the write half of COI-566. An empty pre-selection
          // makes the admin choose, and the field is required, so a blank
          // cannot be saved.
          Role: userOrg?.RoleKey ?? '',
        };

  const { data: getRolesResponse, loading: loadingRoles } =
    useGetAvailableRoles({
      queryArgs: {},
      // V2 single-role mode only — V3 picks from custom roles.
      shouldSkip: isMultiRoleEnabled,
    });
  const { data: getCustomRolesData, loading: loadingCustomRoles } =
    useGetCustomRoles({
      queryArgs: {},
      shouldSkip: !isMultiRoleEnabled,
    });
  const availableRoles = getRolesResponse?.available_roles ?? [];

  const roleOptions = !isMultiRoleEnabled
    ? availableRoles
        .filter(
          (role) =>
            role.name !== 'CustomerSupport' ||
            (callerIsCS && !!user?.IsCustomerSupport)
        )
        .map((role) => ({
          id: role.id,
          value: role.name,
          description: role.description,
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
    : (getCustomRolesData?.custom_role ?? [])
        .map((role) => ({
          id: role.Id,
          value: role.RoleName,
          description: role.Description ?? undefined,
        }))
        .sort((a, b) => a.value.localeCompare(b.value));

  const onSave = async (
    values: UserDetailsFormFields | UserDetailsFormFieldsMultiRole
  ) => {
    if (isMultiRoleEnabled && 'Roles' in values) {
      const { UserId, Roles } = values;

      // Every option on the V3 path is a custom role id. `isUUID` is kept as a
      // guard rather than a filter with meaning — a non-uuid here would be a bug,
      // and silently posting it as a custom role id would 400 at the schema.
      const customRoleIds = Roles.filter((r) => isUUID(r.value)).map(
        (r) => r.value
      );

      await updateCustomRoles({
        userId: UserId,
        customRoleIds,
      });
    } else if (!isMultiRoleEnabled && 'Role' in values) {
      // Single role mode: convert to array for backward compatibility
      const { UserId, Role } = values;
      const roleIds = availableRoles
        .filter((role) => role.name === Role)
        .map((role) => role.id);

      await updateUserRoles({
        userId: UserId,
        roleIds,
      });
    }
  };

  if (loadingUser || loadingRoles || loadingCustomRoles) {
    return null;
  }

  const formId = 'user-form';
  const isReadOnly = false;

  return (
    <ModalForm
      i18n={t('userSettings')}
      values={userValues}
      defaultValues={isMultiRoleEnabled ? { Roles: [] } : { Role: '' }}
      schema={
        isMultiRoleEnabled ? UserDetailsSchemaMultiRole : UserDetailsSchema
      }
      onSave={onSave}
      onDismiss={onDismiss}
      formId={formId}
      visible={true}
      readOnly={isReadOnly}
    >
      <UserDetailsForm
        roleOptions={roleOptions}
        readOnly={isReadOnly}
        multiRoleMode={isMultiRoleEnabled}
      />
    </ModalForm>
  );
};

export default UserDetailsModal;
